import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import ClipLoader from "react-spinners/ClipLoader"; // Import ClipLoader for loading spinner
import styles from "./TicketSalesTable.module.css"; // Import CSS Module

const TicketSalesTable = ({ tableData }) => {
  const [adminId] = useState(localStorage.getItem("adminId"));
  const [role] = useState(localStorage.getItem("userRole"));
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [showModal, setShowModal] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    familyName: "",
    gender: ""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");

  useEffect(() => {
    let timer;
    if (loading) {
      // If loading exceeds 5 seconds, show long wait message
      timer = setTimeout(() => {
        setLongWait(true);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [loading]);

  if (!tableData?.["Ticket Type"]?.length) {
    return <p>No ticket data available</p>;
  }

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" })); // Clear errors on input change
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid.";
    }
    if (!formData.name) {
      newErrors.name = "Name is required.";
    }
    if (!formData.familyName) {
      newErrors.familyName = "Family Name is required.";
    }
    if (!formData.gender) {
      newErrors.gender = "Gender is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setLongWait(false);
    setResponseMessage("");
    setPdfUrl("");

    try {
      const response = await axios.post(
        `${BACKEND_URL}/tickets/create-guest-list`,
        {
          adminId,
          eventId: tableData["Event ID"],
          ticketId: currentTicketId,
          firstName: formData.name,
          lastName: formData.familyName,
          email: formData.email,
          gender: formData.gender
        }
      );
      setResponseMessage(
        `${response.data.message} The email has been successfully sent to the customer.`
      );
      if (response.data.pdfUrl) {
        setPdfUrl(response.data.pdfUrl);
      }
    } catch (error) {
      if (error.response) {
        // Server responded with a status other than 200 range
        setResponseMessage(error.response.data.message || "An error occurred.");
      } else if (error.request) {
        // Request was made but no response received
        setResponseMessage("No response from server.");
      } else {
        // Something else happened
        setResponseMessage("An error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlusButtonClick = (ticketId) => {
    setCurrentTicketId(ticketId);
    setShowModal(true);
  };

  return (
    <>
      <table className={styles.ticketTable}>
        <thead>
          <tr>
            <th>Ticket Type</th>
            <th>Price (€)</th>
            <th>Sold</th>
            <th>Benefits (€)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tableData["Ticket Type"].map((ticketType, index) => {
            const price = tableData["Price (€)"][index];
            const ticketId = tableData["Ticket ID"][index];
            return (
              <tr key={index}>
                <td>{ticketType}</td>
                <td>{price}</td>
                <td>{tableData["Sold"][index]}</td>
                <td>{tableData["Benefits (€)"][index]}</td>
                <td>
                  {price === 0 && (
                    <button
                      className={styles.plusButton}
                      onClick={() => handlePlusButtonClick(ticketId)}
                      aria-label="Add Guest"
                    >
                      +
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {showModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowModal(false)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => setShowModal(false)}
              aria-label="Close Modal"
            >
              &times;
            </button>
            <form onSubmit={handleFormSubmit} className={styles.guestForm}>
              <div className={styles.formGroup}>
                <label htmlFor="email" className={styles.formLabel}>
                  Email:
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={styles.formInput}
                />
                {errors.email && (
                  <div className={styles.errorText}>{errors.email}</div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="name" className={styles.formLabel}>
                  Name:
                </label>
                <input
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={styles.formInput}
                />
                {errors.name && (
                  <div className={styles.errorText}>{errors.name}</div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="familyName" className={styles.formLabel}>
                  Family Name:
                </label>
                <input
                  name="familyName"
                  id="familyName"
                  value={formData.familyName}
                  onChange={handleInputChange}
                  required
                  className={styles.formInput}
                />
                {errors.familyName && (
                  <div className={styles.errorText}>{errors.familyName}</div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="gender" className={styles.formLabel}>
                  Gender:
                </label>
                <select
                  name="gender"
                  id="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: "100%",
                    padding: "5px 20px 5px 20px",
                    border: "1px solid #ccc",
                    borderRadius: "10px",
                    fontSize: "14px",
                    backgroundColor: "#ffffff",
                    fontFamily: "'Poppins', sans-serif",
                    color: "#353943",
                    boxSizing: "border-box"
                  }}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && (
                  <div className={styles.errorText}>{errors.gender}</div>
                )}
              </div>
              <div className={styles.buttonContainer}>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? (
                    <div className={styles.loadingContainer}>
                      <ClipLoader
                        color={"#ffffff"}
                        size={15}
                        loading={loading}
                        className={styles.clipLoader}
                      />
                      <span className={styles.loadingText}>
                        {longWait
                          ? "This might take longer, please be patient..."
                          : "Processing..."}
                      </span>
                    </div>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
              {responseMessage && (
                <div
                  className={`${styles.responseMessage} ${
                    responseMessage.includes("successfully")
                      ? styles.success
                      : styles.error
                  }`}
                >
                  {responseMessage}
                </div>
              )}
              {pdfUrl && (
                <div className={styles.downloadLinkContainer}>
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.downloadLink}
                  >
                    Download Your Ticket
                  </a>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

TicketSalesTable.propTypes = {
  tableData: PropTypes.shape({
    "Ticket Type": PropTypes.arrayOf(PropTypes.string),
    "Price (€)": PropTypes.arrayOf(PropTypes.number),
    Sold: PropTypes.arrayOf(PropTypes.number),
    "Benefits (€)": PropTypes.arrayOf(PropTypes.number),
    "Ticket ID": PropTypes.arrayOf(PropTypes.string)
  }),
  eventId: PropTypes.string.isRequired
};

TicketSalesTable.defaultProps = {
  tableData: null
};

export default TicketSalesTable;
